import React, { useState } from 'react'
import {
  IconButton,
  Menu,
  MenuItem,
  Box,
  Typography,
} from '@mui/material'
import SwapHoriz from '@mui/icons-material/SwapHoriz'
import OpenInNew from '@mui/icons-material/OpenInNew'
import { useNavigate } from 'react-router'
import { getUserRoles } from 'src/mentor/layout/utils'
import { PORTAL_CONFIG } from 'src/shared/config/portalConfig'

export default function PortalSwitcherButton() {
  const navigate = useNavigate()
  const userRoles = getUserRoles()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  // Get available portals based on user roles
  const availablePortals = PORTAL_CONFIG.filter((portal) =>
    portal.roles.some((role) => userRoles?.includes(role))
  )

  // Don't show if only one portal
  if (availablePortals.length <= 1) {
    return null
  }

  const handlePortalMenuClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePortalMenuClose = () => {
    setAnchorEl(null)
  }

  const handlePortalSwitch = (portal) => {
    handlePortalMenuClose()

    setTimeout(() => {
      if (portal.external) {
        window.open(portal.path, '_blank', 'noopener,noreferrer')
      } else if (portal.path === '/') {
        sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
        sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
        sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
        window.location.replace('/')
      } else {
        // Update lastChosenFlow for portals that use it (for dual-role user redirect preference)
        if (portal.id === 'clinic-assistant') {
          localStorage.setItem('lastChosenFlow', 'clinic-assistant')
        } else if (portal.id === 'kiosk') {
          localStorage.setItem('lastChosenFlow', 'kiosk')
        } else if (portal.id === 'mentor') {
          localStorage.setItem('lastChosenFlow', 'mentor')
        } else if (portal.id === 'clinic') {
          localStorage.setItem('lastChosenFlow', 'clinic')
        } else if (portal.id === 'support') {
          localStorage.setItem('lastChosenFlow', 'support')
        } else if (portal.id === 'business') {
          localStorage.setItem('lastChosenFlow', 'business')
        }
        navigate(portal.path)
      }
    }, 100)
  }

  const isCurrentPortal = (portal) => {
    if (portal.external) return false

    const currentPath = window.location.pathname

    if (portal.path === '/') {
      return (
        currentPath === '/' ||
        (!currentPath.startsWith('/support-home') &&
          !currentPath.startsWith('/mentor-home') &&
          !currentPath.startsWith('/clinic-home') &&
          !currentPath.startsWith('/ca-home') &&
          !currentPath.startsWith('/kiosk') &&
          !currentPath.startsWith('/workplace'))
      )
    }

    return currentPath === portal.path || currentPath.startsWith(portal.path + '/')
  }

  return (
    <>
      <IconButton
        onClick={handlePortalMenuClick}
        size="small"
        sx={{
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          color: 'rgba(16, 185, 129, 0.9)',
          width: 36,
          height: 36,
          '&:hover': {
            backgroundColor: 'rgba(16, 185, 129, 0.15)',
            borderColor: 'rgba(16, 185, 129, 0.3)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <SwapHoriz sx={{ fontSize: 20 }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handlePortalMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 280,
            maxWidth: 320,
            borderRadius: '12px',
            border: '1px solid rgba(16, 185, 129, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
          },
        }}
      >
        {availablePortals.map((portal) => {
          const Icon = portal.icon
          const isActive = isCurrentPortal(portal)
          const shouldDisable = isActive && !portal.external && portal.path !== '/'
          return (
            <MenuItem
              key={portal.id}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handlePortalSwitch(portal)
              }}
              disabled={shouldDisable}
              sx={{
                py: 1.25,
                px: 2,
                fontSize: '14px',
                backgroundColor: isActive ? 'rgba(16, 185, 129, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: isActive ? 'rgba(16, 185, 129, 0.12)' : 'rgba(16, 185, 129, 0.06)',
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} width="100%">
                <Icon
                  sx={{
                    fontSize: 20,
                    color: isActive ? 'rgba(16, 185, 129, 0.9)' : 'rgba(0, 0, 0, 0.6)',
                  }}
                />
                <Box flex={1}>
                  <Typography
                    variant="body2"
                    fontWeight={isActive ? 600 : 500}
                    sx={{
                      color: isActive ? 'rgba(16, 185, 129, 0.95)' : 'rgba(0, 0, 0, 0.87)',
                    }}
                  >
                    {portal.name}
                  </Typography>
                </Box>
                {isActive && (
                  <Box
                    sx={{
                      height: '20px',
                      fontSize: '10px',
                      fontWeight: 600,
                      padding: '0 6px',
                      display: 'flex',
                      alignItems: 'center',
                      background: 'rgba(16, 185, 129, 0.15)',
                      color: 'rgba(16, 185, 129, 0.9)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '10px',
                    }}
                  >
                    Current
                  </Box>
                )}
                {portal.external && (
                  <OpenInNew
                    sx={{
                      fontSize: 16,
                      color: 'rgba(0, 0, 0, 0.5)',
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}

